import { paths } from '@common/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import { schemaAggregator } from 'components/DynamicFormFields/schema/schemaAggregator';
import { toast } from 'react-toastify';
import { debounce } from '@util/debounce.ts';
import { useState } from 'react';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useSaveExternalAssessmentMutation } from '@common/cache';

interface FormShape {
  [field_name: string]: string;
}

interface IUseAssessmentProps {
  token: string;
  assessment_id: string;
  assessment: paths[`/patient/{patient_id}/assessment/{assessment_id}`]['get']['responses']['200']['content']['application/json'];
}

export const useAssessment = ({
  token,
  assessment_id,
  assessment,
}: IUseAssessmentProps) => {
  const saveAssessmentMutation = useSaveExternalAssessmentMutation({
    assessment_id,
    token,
  });

  const requirements = assessment.requirements || [];

  const flattenedRequirements = requirements.flatMap(req => ({
    name: req.field_name,
    value: req.field_value,
    type: req.type,
    optional: req.optional,
    disable_if: req.disable_if,
  }));

  const initialValues = flattenedRequirements.reduce((acc, current) => {
    acc[current.name] = current.value;
    return acc;
  }, Object.create({}));

  const schema = schemaAggregator(flattenedRequirements);

  const formMethods = useForm<FormShape>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  const watchedValues = useWatch({ control: formMethods.control });

  // eslint-disable-next-line react-hooks/immutability -- intentional: updating RHF context for cross-field yup validation, this is the documented pattern
  formMethods.control._options.context = { values: watchedValues };

  const { getValues, setValue } = formMethods;
  const [fieldName, setFieldName] = useState('');
  const [isSavingAssessment, setIsSavingAssessment] = useState(false);

  const handleChangeField = async (nameList: string[]) => {
    if (nameList.length > 0) {
      setIsSavingAssessment(true);

      const shouldClearInterval = nameList.includes(fieldName);
      const formPayload = nameList.reduce(
        (acc, curr) => ({ ...acc, [curr]: getValues(curr) }),
        {},
      );

      setFieldName(nameList[0]);

      debounce(async () => {
        try {
          const response = await saveAssessmentMutation.mutateAsync({
            form_data: formPayload,
          });

          if (response.data && response.data.updated_fields?.length > 0) {
            const { updated_fields } = response.data;
            updated_fields.forEach(updatedField => {
              setValue(updatedField.field_name, updatedField.field_value ?? '');
            });
          }
        } catch (error) {
          toast.error(formatApiError(error));
        } finally {
          setIsSavingAssessment(false);
        }
      }, shouldClearInterval);
    }
  };

  return {
    formMethods,
    isSavingAssessment,
    handleChangeField,
  };
};
